import { Application, Lead } from "@justhome/common/api/core";
import { useIsMobile } from "@justhome/common/hooks";
import { GTM_EVENT_CONTACT_WALL } from "@lib/constants";
import gtm from "@lib/gtm";
import useLinks from "@lib/hooks/useLinks";
import usePartnerContext from "@lib/hooks/usePartnerContext";
import { ApplicationFormValues } from "@lib/mapApplicationData/types";
import useApplicationContext from "@modules/application/hooks/useApplicationContext";
import useLeadWallSlides from "@modules/application/hooks/useLeadWallSlides";
import usePostApplicationWithLead from "@modules/application/hooks/usePostApplicationWithLead";
import { useRouter } from "next/router";
import { FC, useEffect, useState } from "react";
import ApplicationLayout from "../ApplicationLayout";
import { FormStepComponent } from "../FormSteps/FormSteps";
import Slideshow from "../Slideshow";
import SwiperCard from "../SwiperCard";
import LeadWallFirstStep from "./LeadWallFirstStep";
import LeadWallSecondStep from "./LeadWallSecondStep";

interface LeadWallProps extends FormStepComponent<ApplicationFormValues> {
  isCertificate?: boolean;
}

const LeadWall: FC<LeadWallProps> = ({ isCertificate, value }) => {
  const { state, dispatch } = useApplicationContext();
  const { partner } = usePartnerContext();
  const [isFirstStepDone, setIsFirstStepDone] = useState(false);
  const isMobile = useIsMobile();
  const isTwoStepLeadWall = isMobile && !isCertificate;
  const router = useRouter();
  const [email, setEmail] = useState("");
  const { getApplicationProcessLink } = useLinks();
  const slides = useLeadWallSlides(isCertificate);

  useEffect(() => {
    gtm.event(GTM_EVENT_CONTACT_WALL);
  }, []);

  const handleGoBack = () => {
    router.back();
  };

  const handleFirstStepSuccess = (application: Application, lead: Lead) => {
    dispatch({ type: "SET_APPLICATION", application });

    if (isTwoStepLeadWall) {
      setEmail(lead.email!);
      setIsFirstStepDone(true);
    } else {
      dispatch({ type: "SET_LEAD", lead });
      router.push(
        getApplicationProcessLink(
          lead.isOffersViewForbidden ? "forbidden" : "offers",
        ),
      );
    }
  };

  const handleSecondStepPrevious = () => {
    setIsFirstStepDone(false);
  };

  const handlerSecondStepSuccess = (lead: Lead) => {
    dispatch({ type: "SET_LEAD", lead });
    router.push(
      getApplicationProcessLink(
        lead.isOffersViewForbidden ? "forbidden" : "offers",
      ),
    );
  };

  const { isLoading, isError, handleSubmit } = usePostApplicationWithLead({
    partnerId: partner?.id,
    leadCreationAction: isCertificate
      ? "FINANCING_CERTIFICATE"
      : "CONTACT_WALL",
    applicationForm: value as ApplicationFormValues,
    onSuccess: handleFirstStepSuccess,
  });

  return (
    <ApplicationLayout
      border
      centered={false}
      className="h-full py-2s pb-xl m:pb-none m:grid grid-cols-12 gap items-center"
    >
      <div className="col-span-5">
        {!isFirstStepDone && (
          <LeadWallFirstStep
            onSubmit={handleSubmit}
            isError={isError}
            isLoading={isLoading}
            isCertificate={isCertificate}
            isPartner={!!partner}
            isEmailOnly={isTwoStepLeadWall}
            defaultValues={state.lead}
            slides={slides}
            onPrevious={handleGoBack}
          />
        )}
        {isFirstStepDone && (
          <LeadWallSecondStep
            email={email}
            applicationId={state.application!.id!}
            onPrevious={handleSecondStepPrevious}
            onSuccess={handlerSecondStepSuccess}
            leadCreationAction={
              isCertificate ? "FINANCING_CERTIFICATE" : "CONTACT_WALL"
            }
          />
        )}
      </div>
      <div className="mb-3s col-span-6 col-start-7 hidden m:flex h-full max-h-[1000px]">
        <Slideshow>
          {slides.map((slide) => (
            <SwiperCard key={slide.title} {...slide} />
          ))}
        </Slideshow>
      </div>
    </ApplicationLayout>
  );
};

export default LeadWall;
